<template>
  <div
    v-if="show"
    class="price-adjust-v2-overlay fade-in"
    @click.self="handleClose"
  >
    <div class="price-adjust-v2-panel">
      <!-- Header -->
      <div class="pa-header">
        <h2 class="pa-title">
          Adjust Price
        </h2>
        <button
          class="pa-close-btn"
          @click="handleClose"
        >
          <KioskIcon
            name="close"
            size="24"
            stroke-width="2"
          />
        </button>
      </div>

      <!-- Price Display Area -->
      <div class="pa-display-section">
        <div class="pa-price-card">
          <span class="pa-currency">{{ currency }}</span>
          <span class="pa-amount">{{ formattedPrice }}</span>
        </div>

        <!-- Tax Toggle -->
        <div
          v-if="hasTax"
          class="pa-tax-toggle mt-4"
        >
          <button
            class="pa-toggle-btn"
            :class="{ active: isTaxed }"
            @click="isTaxed = !isTaxed"
          >
            <div class="toggle-track">
              <div class="toggle-thumb" />
            </div>
            <span class="toggle-label">{{
              isTaxed ? "Tax Included" : "No Tax"
            }}</span>
          </button>
        </div>
      </div>

      <!-- Numeric Keypad -->
      <div class="pa-keypad">
        <SimpleNumKeyboard
          :withconfirm="false"
          :amount="priceStr"
          @updated="setPriceStr"
        />
      </div>

      <!-- Action Footer -->
      <div class="pa-footer">
        <button
          class="pa-confirm-btn"
          @click="handleConfirm"
        >
          Confirm Price
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SimpleNumKeyboard from "../../components/SimpleNumKeyboard";
import KioskIcon from "./components/KioskIcon";

export default {
  name: "ModalPriceAdjustV2",
  components: {
    SimpleNumKeyboard,
    KioskIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      priceStr: "0",
      isTaxed: false,
      hasTax: false,
      product: null,
      item: null,
    };
  },
  computed: {
    ...mapGetters({
      currency: "config/currency",
    }),
    formattedPrice() {
      const val = parseFloat(this.priceStr) / 100;
      return val.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initData();
      }
    },
  },
  methods: {
    initData() {
      this.item = this.$store.getters["itemPriceAdjust/item"];
      if (this.item) {
        this.product = this.$store.getters["config/product"](this.item.product);

        // Use item price, or fallback to product price if item price is 0
        const initialPrice =
          this.item.price && this.item.price !== 0
            ? this.item.price
            : this.product
            ? this.product.price
            : 0;

        this.priceStr = Math.round((initialPrice || 0) * 100).toString();
        this.isTaxed = this.item.tax_status === 0;
        this.hasTax =
          this.product &&
          this.product.tax_rates &&
          this.product.tax_rates.length > 0;
      }
    },
    setPriceStr(val) {
      this.priceStr = val || "0";
    },
    handleClose() {
      this.$emit("close");
    },
    handleConfirm() {
      const finalPrice = parseFloat(this.priceStr) / 100;
      this.$emit("savePrice", {
        price: finalPrice,
        taxed: this.isTaxed,
        product: this.product,
      });
      this.handleClose();
    },
  },
};
</script>

<style scoped>
.price-adjust-v2-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.price-adjust-v2-panel {
  background: white;
  width: 90%;
  max-width: 440px;
  border-radius: 32px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
}

.pa-header {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
}

.pa-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.pa-close-btn {
  background: #f1f5f9;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.pa-close-btn:active {
  transform: scale(0.9);
}

.pa-display-section {
  padding: 16px 24px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pa-price-card {
  background: white;
  padding: 12px 32px;
  border-radius: 20px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: baseline;
  gap: 8px;
  border: 2px solid #e2e8f0;
}

.pa-currency {
  font-size: 20px;
  font-weight: 600;
  color: #64748b;
}

.pa-amount {
  font-size: 36px;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.pa-tax-toggle {
  display: flex;
  justify-content: center;
}

.pa-toggle-btn {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 4px 12px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.pa-toggle-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
}

.toggle-track {
  width: 36px;
  height: 20px;
  background: #e2e8f0;
  border-radius: 100px;
  position: relative;
  transition: all 0.3s;
}

.pa-toggle-btn.active .toggle-track {
  background: #2563eb;
}

.toggle-thumb {
  width: 14px;
  height: 14px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pa-toggle-btn.active .toggle-thumb {
  left: 19px;
}

.toggle-label {
  font-weight: 700;
  font-size: 13px;
  color: #475569;
}

.pa-toggle-btn.active .toggle-label {
  color: #2563eb;
}

.pa-keypad {
  padding: 16px 24px;
  background: white;
}

/* Override existing keyboard styles to match V2 look */
.pa-keypad >>> .num-key {
  height: 64px;
  background: #f8fafc;
  border: none;
  border-radius: 16px;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  display: flex !important;
  align-items: center;
  justify-content: center;
  margin: 0;
  box-shadow: none;
  transition: all 0.2s;
}

.pa-keypad >>> .num-key:active {
  background: #e2e8f0;
  transform: scale(0.95);
}

.pa-keypad >>> .pin-keyboard-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.pa-keypad >>> .pin-keyboard-list li {
  margin: 0;
  padding: 0;
  width: 100%;
}

.pa-keypad >>> .pin-key_call {
  background: #f1f5f9;
}

.pa-footer {
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #f1f5f9;
}

.pa-confirm-btn {
  width: 100%;
  padding: 14px;
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.2);
}

.pa-confirm-btn:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
